import { Directive, ElementRef, Input, HostListener, Renderer2 } from '@angular/core';
import { ApBaseControlValueAccessor } from '../base-control/index';

// regex to match any integer
const integerRegex = new RegExp(`^\\-?[0-9]*$`);

// regex to match a positive integer
const positiveIntegerRegex = new RegExp(`^[0-9]+$`);

// regex to match any decimal
const decimalRegex = new RegExp(`^\\-?[0-9]*\\.?[0-9]*$`);

// regex to match a positive decimal
const positiveDecimalRegex = new RegExp(`^[0-9]*\\.?[0-9]*$`);

// regex to match a positive decimal, or fraction of positive decimals
const positiveDecimalFractionRegex = new RegExp(`^[0-9\\/\\.]*$`);

@Directive({
  selector: 'input[apNumberValidation]'
})
export class ApNumberValidationDirective extends ApBaseControlValueAccessor<string | null> {
  @Input('apNumberValidation') validationType: string;

  // store the previous valid value
  private previousValue = '';

  constructor(private el: ElementRef, private renderer: Renderer2) {
    super();
  }

  @HostListener('blur', ['$event'])
  onBlur(_event: any) {
    const value: string = this.el.nativeElement.value;
    if (!value) {
      return;
    }
    if (value.length === 1) {
      return;
    }

    // Remove any leading zeros from input value
    let trimmedValue = '';
    switch (this.validationType) {
      case 'integer':
      case 'positiveInteger':
        trimmedValue = parseInt(value, 10).toString();
        break;
      case 'decimal':
      case 'positiveDecimal':
        trimmedValue = parseFloat(value).toString();
        break;
      case 'positiveDecimalFraction':
        if (value.includes('/')) {
          const fractionParts = value.split('/');
          const numerator = parseFloat(fractionParts[0]).toString();
          const denominator = parseFloat(fractionParts[1]).toString();
          trimmedValue = numerator + '/' + denominator;
        } else {
          trimmedValue = parseFloat(value).toString();
        }
        break;
    }
    this.tryAssignValueProgrammatically(trimmedValue);
  }

  @HostListener('input', ['$event'])
  onInput(_event: any) {
    const value: string = this.el.nativeElement.value;
    this.onInputChange(value);
  }

  private onInputChange(value: string | null) {
    let regex: RegExp | undefined;
    switch (this.validationType) {
      case 'decimal':
        regex = decimalRegex;
        break;
      case 'integer':
        regex = integerRegex;
        break;
      case 'positiveDecimal':
        regex = positiveDecimalRegex;
        break;
      case 'positiveInteger':
        regex = positiveIntegerRegex;
        break;
      case 'positiveDecimalFraction':
        regex = positiveDecimalFractionRegex;
        break;
    }
    if (!regex) { return; }

    if (value && !regex.test(value)) {
      // if the input value doesn't match the regex, replace it with the previous valid value
      this.renderer.setProperty(this.el.nativeElement, 'value', this.previousValue);
    } else {
      // store the previous valid value
      this.previousValue = value || '';
    }

    this.propagateChangesFromView(this.previousValue);
  }

  protected override tryAssignValueProgrammatically(newValue: string | null): void {
    this.onInputChange(newValue);

    const element = this.el.nativeElement as HTMLInputElement;
    this.renderer.setProperty(element, 'value', this.previousValue);
  }
}
